import {
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Stack,
  SxProps,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'

import {
  ConsoleEventTicketInfoBoxFragment,
  EventStatus,
} from '@/clients/utoniq-core/schema'
import { EventCategory } from '@/components/organisms'
import { styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Organism } from '@/core/architecture'

type ConsoleEventTicketInfoBoxProps = {
  event: ConsoleEventTicketInfoBoxFragment
  width?: number | string
}

export const ConsoleEventTicketInfoBox: Organism<ConsoleEventTicketInfoBoxProps> =
  ({ event, width }) => {
    const { t } = useTranslation('consoleHome')
    const { routerPages, generatePath } = useRouter()
    return (
      <Card sx={{ width, borderRadius: 2 }}>
        <CardActionArea
          sx={{ height: '100%' }}
          href={generatePath(routerPages.ConsoleShokufesEventDetail, {
            eventId: event.id,
          })}
        >
          <CardContent sx={{ p: 5, '&:last-child': { pb: 5 } }}>
            <Stack direction="row" spacing={2} pb={1}>
              <EventCategory event={event as never} />
              <EventStatusTag status={event.publishStatus} />
            </Stack>
            <Stack pb={3} spacing={1}>
              <Typography variant="subtitle1" sx={styleSets.lineCamp(2)}>
                {event.title}
              </Typography>
              <Stack spacing="2px">
                {!!event.dateRange && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={styleSets.lineCamp(2)}
                  >
                    {t('date')}
                    {event.dateRange}
                  </Typography>
                )}
                {event.location && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={styleSets.lineCamp(1)}
                  >
                    {t('address')}
                    {event.location?.approximateAddress}
                  </Typography>
                )}
              </Stack>
            </Stack>
            <Divider />
            <Stack direction="row" pt={3} spacing={3}>
              <TicketPurchaseStatus
                {...event?.preSalesAggregation?.aggregation?.purchases}
              />
              <Divider orientation="vertical" flexItem />
              <TicketUsageStatus
                {...event?.preSalesAggregation?.aggregation?.ticketUsage}
                {...event?.preSalesAggregation?.aggregation?.purchases}
              />
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    )
  }

type EventStatusTagProps = {
  status: EventStatus
}
export const EventStatusTag: Organism<EventStatusTagProps> = ({ status }) => {
  const { t } = useTranslation('consoleHome')
  const { bgcolor, color, text } = useMemo(() => {
    if (status === EventStatus.Published) {
      return {
        bgcolor: 'primary.main',
        color: 'white',
        text: t('available', '公開中'),
      }
    }
    return {
      bgcolor: 'grey.100',
      color: 'text.disabled',
      text: t('private', '非公開'),
    }
  }, [status, t])
  return (
    <Chip
      size="small"
      sx={{
        height: 20,
        borderRadius: '2px',
        minWidth: 40,
        bgcolor,
        '.MuiChip-label': {
          px: 1,
        },
      }}
      label={
        <Typography color={color} variant="overline" component="p">
          {text}
        </Typography>
      }
    />
  )
}

type TicketPurchaseStatusProps = {
  purchasedNumber?: number
  issuedNumber?: number
  sx?: SxProps
}
export const TicketPurchaseStatus: Organism<TicketPurchaseStatusProps> = ({
  purchasedNumber,
  issuedNumber,
  sx,
}) => {
  const { t } = useTranslation('consoleHome')
  return (
    <Stack flex={1} alignItems="center" spacing={1} sx={sx}>
      <Typography variant="subtitle2" align="left" width="100%">
        {t('purchaseStatus', '購入状況')}
      </Typography>
      <Stack direction="row" alignItems="center">
        <Typography variant="h5" color="secondary.main">
          {purchasedNumber || '-'}
        </Typography>
        <Typography variant="h6">/</Typography>
        <Typography variant="subtitle2" pt="2px">
          {issuedNumber || '-'}
        </Typography>
      </Stack>
      <Typography variant="overline" color="text.secondary">
        {t('purchaseStatusNote', '購入数/在庫数')}
      </Typography>
    </Stack>
  )
}

type TicketUsageStatusProps = {
  ticketUsedCount?: number
  ticketUnusedCount?: number
  purchasedNumber?: number
  sx?: SxProps
}
export const TicketUsageStatus: Organism<TicketUsageStatusProps> = ({
  ticketUsedCount,
  ticketUnusedCount,
  purchasedNumber,
  sx,
}) => {
  const { t } = useTranslation('consoleHome')
  return (
    <Stack flex={1} alignItems="center" spacing={1} sx={sx}>
      <Typography variant="subtitle2" align="left" width="100%">
        {t('usageStatus', '使用状況')}
      </Typography>
      <Stack direction="row" alignItems="center">
        <Typography variant="h5" color="secondary.main">
          {ticketUsedCount || '-'}
        </Typography>
        <Typography variant="overline" color="warning.main" pt={1}>
          ({ticketUnusedCount || '-'})
        </Typography>
        <Typography variant="h6">/</Typography>
        <Typography variant="subtitle2" pt="2px">
          {purchasedNumber || '-'}
        </Typography>
      </Stack>
      <Typography variant="overline" color="text.secondary">
        {t('usageStatusNote', '使用済み(未使用)/購入数')}
      </Typography>
    </Stack>
  )
}
